import { useState, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';

const TIMER_DURATION = 10 * 60; // 10 minutes in seconds
const STORAGE_KEY = 'cart_timer_start';

interface Props {
  onExpire?: () => void;
}

export function CartCountdownTimer({ onExpire }: Props) {
  const getRemainingTime = useCallback(() => {
    try {
      const startTime = localStorage.getItem(STORAGE_KEY);
      if (!startTime) {
        // Start a new timer
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        return TIMER_DURATION;
      }
      const elapsed = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      const remaining = TIMER_DURATION - elapsed;
      return Math.max(0, remaining);
    } catch {
      return TIMER_DURATION;
    }
  }, []);

  const [remaining, setRemaining] = useState(getRemainingTime);

  useEffect(() => {
    const interval = setInterval(() => {
      const time = getRemainingTime();
      setRemaining(time);
      if (time <= 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [getRemainingTime, onExpire]);

  if (remaining <= 0) return null;

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  const isUrgent = remaining < 120; // Last 2 minutes

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
        isUrgent
          ? 'bg-red-50 text-red-700 border border-red-200'
          : 'bg-amber-50 text-amber-700 border border-amber-200'
      }`}
    >
      <Clock className={`h-4 w-4 ${isUrgent ? 'animate-pulse' : ''}`} />
      <span>
        Items reserved for{' '}
        <span className="font-bold tabular-nums">
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </span>
      </span>
    </div>
  );
}

/**
 * Reset the cart timer (e.g., after adding an item or completing checkout).
 */
export function resetCartTimer(): void {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  } catch {
    // ignore
  }
}

/**
 * Clear the cart timer.
 */
export function clearCartTimer(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}